type Rgb = { r: number; g: number; b: number };

const clamp8 = (n: number) => Math.max(0, Math.min(255, n));

export const hexToRgb = (hex: unknown): Rgb | undefined => {
    if (typeof hex !== 'string') return undefined;
    const trimmed = hex.trim();
    const match = trimmed.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!match) return undefined;

    const fullHex = match[1].length === 3
        ? `${match[1][0]}${match[1][0]}${match[1][1]}${match[1][1]}${match[1][2]}${match[1][2]}`
        : match[1];

    const bigint = parseInt(fullHex, 16);
    return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
    };
};

export const cssRgbToRgb = (color: unknown): Rgb | undefined => {
    if (typeof color !== 'string') return undefined;
    const trimmed = color.trim();
    const match = trimmed.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
    if (!match) return undefined;

    return {
        r: clamp8(Number(match[1])),
        g: clamp8(Number(match[2])),
        b: clamp8(Number(match[3])),
    };
};

// Perceived luminance (0 = black .. 255 = white) using the standard weights.
export const luminanceOf = (color: unknown): number | undefined => {
    const rgb = hexToRgb(color) ?? cssRgbToRgb(color);
    if (!rgb) return undefined;
    return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
};

export const isDarkColor = (color: unknown) => {
    const luminance = luminanceOf(color);
    if (luminance === undefined) {
        // Unknown format (e.g. CSS var). Treat as "not dark" to avoid forcing dark styles unexpectedly.
        return false;
    }
    return luminance < 128;
};

// Pick a legible foreground for the given background. Keeps `preferred` when it has
// enough luminance separation from the background to stay visible; otherwise falls
// back to black/white chosen by the background's luminance. When the background
// can't be parsed (gradient, CSS var), the merchant's `preferred` color is trusted
// as-is. Used so header controls (e.g. the cart icon) never end up dark-on-dark.
export const readableOn = (background: unknown, preferred?: string): string => {
    const bgLum = luminanceOf(background);
    if (bgLum === undefined) return preferred ?? '#1a1a1a';
    const fallback = bgLum < 128 ? '#ffffff' : '#1a1a1a';
    const prefLum = luminanceOf(preferred);
    if (prefLum === undefined) return fallback;
    return Math.abs(prefLum - bgLum) >= 60 ? (preferred as string) : fallback;
};

// Deterministic, colorful chip color for a product tag, derived from a hash of its value
// so each distinct tag ("AI Ready", "New", …) gets its own hue with a slight per-value
// offset in saturation/lightness. Returns a mid-dark background + a readable text color.
export const tagColor = (value: string): { background: string; color: string } => {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = (hash * 31 + value.charCodeAt(i)) | 0;
    }
    const hue = Math.abs(hash) % 360;
    const sat = 60 + (Math.abs(hash >> 3) % 18); // 60–77%
    const light = 40 + (Math.abs(hash >> 7) % 12); // 40–51% (kept dark enough for white text)
    return { background: `hsl(${hue}, ${sat}%, ${light}%)`, color: '#ffffff' };
};
